class ProductInformation extends HTMLElement {
  constructor() {
    super();
    this.sectionId = this.dataset.sectionId;
  }

  connectedCallback() {
    this.querySelector('pinfo-variant-picker')?.addEventListener(
      'input',
      this.handleVariantEvent.bind(this)
    );
    this.initQuantitySelector();
    this.initVariantPickerTapFix();
    this.initSwiper();
    this.initProductZoomModalTriggers();
    this.initTrustBadgeSwiper();
    this.initSellingPlan();
    this.initAccordion();
    this.bindFormSubmit();
    this.bindBuyNow();
  }

  // product | json — used for media, variant picker, price block
  get productJSON() {
    if (!this._productJSON) {
      const el = this.querySelector('script[data-product-json]');
      this._productJSON = el ? JSON.parse(el.textContent) : null;
    }
    return this._productJSON;
  }

  get spAllocations() {
    if (!this._spAllocations) {
      const product = this.productJSON;
      if (product?.variants) {
        const variants = {};
        product.variants.forEach(v => {
          const allocations = {};
          (v.selling_plan_allocations || []).forEach(a => {
            allocations[String(a.selling_plan_id)] = {
              price: a.price,
              compare_at_price: a.compare_at_price
            };
          });
          variants[String(v.id)] = { price: v.price, allocations };
        });
        this._spAllocations = { variants };
      } else {
        const el = document.getElementById('sp-allocations-json');
        try {
          this._spAllocations = el ? JSON.parse(el.textContent) : { variants: {} };
        } catch (e) {
          console.warn('sp-allocations-json parse error', e);
          this._spAllocations = { variants: {} };
        }
      }
    }
    return this._spAllocations;
  }

  isMobileProductViewport() {
    return window.matchMedia('(max-width: 989px)').matches;
  }

  getVariantIdFromUrl(url) {
    if (typeof url !== 'string') return null;

    const query = url.includes('?') ? url.split('?')[1] : '';
    const variantId = Number(new URLSearchParams(query).get('variant'));

    return Number.isFinite(variantId) && variantId > 0 ? variantId : null;
  }

  getVariantFromUrl(url) {
    const variantId = this.getVariantIdFromUrl(url);
    if (!variantId || !this.productJSON?.variants) return null;

    return this.productJSON.variants.find((variant) => Number(variant.id) === variantId) || null;
  }

  selectVariantInput(input) {
    if (!(input instanceof HTMLInputElement) || input.disabled || input.checked) return;

    input.checked = true;
    input.blur?.();
    input.dispatchEvent(new Event('input', { bubbles: true }));
  }

  updatePriceForVariant(variant) {
    if (!variant) return;

    const price = this.formatMoney(variant.price);
    const compareAtPrice = variant.compare_at_price && variant.compare_at_price > variant.price
      ? this.formatMoney(variant.compare_at_price)
      : '';

    this.querySelectorAll('.pinfo-price-wrapper .pinfo-price').forEach((priceEl) => {
      if (compareAtPrice) {
        priceEl.innerHTML = `
          <span class="pinfo-regular-price sale-price">${price}</span>
          <span class="pinfo-compare-price"><s>${compareAtPrice}</s></span>
        `;
      } else {
        priceEl.innerHTML = `<span class="pinfo-regular-price">${price}</span>`;
      }
    });
  }

  updateFormForVariant(variant) {
    if (!variant) return;

    this.querySelectorAll('input[name="id"]').forEach((input) => {
      input.value = variant.id;
    });

    const addButton = this.querySelector('.product-form__submit.pinfo-add-to-cart');
    const addButtonText = addButton?.querySelector('.atc-text');
    const buyNowButton = this.querySelector('.p-info-btn-buy-now');
    const isAvailable = Boolean(variant.available);

    if (addButton) {
      if (!addButton.dataset.defaultText && addButtonText?.textContent?.trim() && addButtonText.textContent.trim() !== 'Sold Out') {
        addButton.dataset.defaultText = addButtonText.textContent.trim();
      }

      addButton.disabled = !isAvailable;
      addButton.classList.toggle('sold-out', !isAvailable);

      if (addButtonText) {
        addButtonText.textContent = isAvailable ? (addButton.dataset.defaultText || 'Add To Cart') : 'Sold Out';
      }
    }

    if (buyNowButton) {
      buyNowButton.disabled = !isAvailable;
    }
  }

  updateVariantClientSide(variant, url, options = {}) {
    if (!variant) return false;

    const { slideMedia = true } = options;

    this.updatePriceForVariant(variant);
    this.updateFormForVariant(variant);
    this.updateSellingPlanPrices(variant.id);
    document.dispatchEvent(new CustomEvent('variant:change', {
      detail: { variantId: variant.id, variant }
    }));

    if (slideMedia && !this.isMobileProductViewport()) {
      this.slideToVariantMedia(variant.id, 300);
    }

    this.updateURL(url);
    return true;
  }

  get mediaOrder() {
    if (!this._mediaOrder) {
      this._mediaOrder = Array.from(
        this.querySelectorAll('.main-swiper .swiper-slide')
      ).map(s => s.dataset.media);
    }
    return this._mediaOrder;
  }

  initTrustBadgeSwiper() {
    const container = this.querySelector('.pinfo-trust-badges-swiper');
    if (!container) return;

    new Swiper(container, {
      slidesPerView: 'auto',
      spaceBetween: 30,
      loop: true,
      freeMode: false,
      allowTouchMove: false,
      speed: 5000, // higher = slower smooth scroll
      autoplay: {
        delay: 0,
        disableOnInteraction: false,
      },
    });
  }

  initVariantPickerTapFix() {
    const picker = this.querySelector('pinfo-variant-picker');
    if (!picker || picker.dataset.mobileTapFix === 'true') return;
    picker.dataset.mobileTapFix = 'true';

    const selectFromLabel = (event) => {
      if (!this.isMobileProductViewport()) return;

      const label = event.target?.closest?.('label.size-card-label');
      if (!label) return;

      const input = label.htmlFor ? document.getElementById(label.htmlFor) : null;
      if (!input || input.disabled || input.checked) return;

      // Stop the browser from focusing/scrolling the hidden radio input on mobile.
      // The product state is updated manually in handleVariantEvent instead.
      event.preventDefault();
      event.stopImmediatePropagation();

      this.selectVariantInput(input);
    };

    picker.addEventListener('touchend', selectFromLabel, { capture: true, passive: false });
    picker.addEventListener('click', selectFromLabel, true);
  }

  handleVariantEvent(e) {
    const target = e.target;
    const variantUrl = target?.dataset?.variantUrl;
    if (!variantUrl) return;

    if (target instanceof HTMLElement) target.blur();

    const variant = this.getVariantFromUrl(variantUrl);

    // Mobile no-jump path: do not fetch/re-render the section and do not move the
    // gallery. Re-rendering content above the size selector is what creates the
    // small visible page movement on mobile.
    if (this.isMobileProductViewport() && variant) {
      this.updateVariantClientSide(variant, variantUrl, { slideMedia: false });
      return;
    }

    this.handleOptions(variantUrl);

    if (variant) {
      this.slideToVariantMedia(variant.id, 300);
    }
  }

  slideToVariantMedia(variantId, speed = 300) {
    const product = this.productJSON;
    if (!product) return;

    const variant = product.variants.find(v => Number(v.id) === Number(variantId));
    if (!variant || !variant.featured_media) return;

    const mediaId = String(variant.featured_media.id);
    const idx = this.mediaOrder.indexOf(mediaId);

    if (idx > -1 && this.mainSwiper) {
      this.mainSwiper.slideTo(idx, speed);
    }
  }

  // ─── Quantity Selector ───────────────────────────────────────────
  initQuantitySelector() {
    const wrapper = this.querySelector('[data-quantity-selector]');
    if (!wrapper) return;
    const input = wrapper.querySelector('.qty-input');
    const dec = wrapper.querySelector('.decrease');
    const inc = wrapper.querySelector('.increase');
    const update = () => this.dispatchQuantityChange(input.value);
    dec?.addEventListener('click', () => {
      let v = parseInt(input.value) || 1;
      if (v > (parseInt(input.min) || 1)) { input.value = v - 1; update(); }
    });
    inc?.addEventListener('click', () => {
      input.value = (parseInt(input.value) || 1) + 1;
      update();
    });
    input?.addEventListener('input', () => {
      if (input.value < 1) input.value = 1;
      update();
    });
  }

  dispatchQuantityChange(value) {
    this.dispatchEvent(new CustomEvent('quantity-change', {
      detail: { quantity: Number(value) },
      bubbles: true
    }));
  }

  // ─── Swiper Init ─────────────────────────────────────────────────
  initSwiper() {
    const container = this.querySelector('.pdp-media-container');
    if (!container) return;

    // Destroy existing swipers before re-init
    if (this.mainSwiper) {
      this.mainSwiper.destroy(true, true);
      this.mainSwiper = null;
    }

    if (this.thumbSwiper) {
      this.thumbSwiper.destroy(true, true);
      this.thumbSwiper = null;
    }

    const mainEl = container.querySelector('.main-swiper');
    const thumbEl = container.querySelector('.thumb-swiper');
    const paginationEl = this.querySelector('.main-swiper-pagination');

    if (!mainEl) return;

    // ── Thumb Swiper (Horizontal Mobile / Vertical Desktop) ──
    if (thumbEl) {
      this.thumbSwiper = new Swiper(thumbEl, {
        slidesPerView: 'auto',
        spaceBetween: 6,
        watchSlidesProgress: true,
        freeMode: true,
        slideToClickedSlide: true,

        // Default mobile direction
        direction: 'horizontal',

        // Desktop breakpoint
        breakpoints: {
          990: {
            direction: 'vertical',
          }
        }
      });

      // ── Thumb Navigation Buttons ──
      const thumbWrapper = container.querySelector('.thumb-swiper-wrapper');

      const prevBtn = thumbWrapper?.querySelector('.thumb-nav-prev');
      const nextBtn = thumbWrapper?.querySelector('.thumb-nav-next');

      prevBtn?.addEventListener('click', () => {
        this.thumbSwiper.slidePrev(200);
      });

      nextBtn?.addEventListener('click', () => {
        this.thumbSwiper.slideNext(200);
      });

      this._thumbPrevBtn = prevBtn;
      this._thumbNextBtn = nextBtn;

      this.thumbSwiper.on('init slideChange resize', () => {
        this.updateThumbNavButtons();
      });

      this.updateThumbNavButtons();
    }

    // ── Main Swiper Config ──
    const mainSwiperConfig = {
      loop: false,
      touchStartPreventDefault: false,
      passiveListeners: false,

      pagination: paginationEl
        ? {
            el: paginationEl,
            clickable: true,
          }
        : false,

      on: {
        slideChange: () => {
          if (this.mainSwiper?.zoom?.out) {
            this.mainSwiper.zoom.out();
          }
          this.updateMainNavButtons();
          this.syncThumbSwiperToMain(220);
        },

        init: () => {
          this.updateMainNavButtons();
          window.requestAnimationFrame(() => this.syncThumbSwiperToMain(0));
        },
      }
    };

    // ── Link thumbs swiper ──
    if (this.thumbSwiper) {
      mainSwiperConfig.thumbs = {
        swiper: this.thumbSwiper
      };
    }

    // ── Init Main Swiper ──
    this.mainSwiper = new Swiper(mainEl, mainSwiperConfig);


    // ── Main Navigation Arrows ──
    const prevArrow = mainEl.querySelector('.swiper-nav-prev');
    const nextArrow = mainEl.querySelector('.swiper-nav-next');

    prevArrow?.addEventListener('click', () => {
      this.mainSwiper.slidePrev(300);
    });

    nextArrow?.addEventListener('click', () => {
      this.mainSwiper.slideNext(300);
    });

    this._prevBtn = prevArrow;
    this._nextBtn = nextArrow;
  }

  // ─── Full-screen Product Image Viewer ─────────────────────────────
  initProductZoomModalTriggers() {
    const modal = document.querySelector(`[data-pdp-zoom-modal="${this.sectionId}"]`);
    const mainEl = this.querySelector('.main-swiper');

    if (!modal || !mainEl || modal.dataset.zoomTriggersInitialized === 'true') return;

    modal.dataset.zoomTriggersInitialized = 'true';
    this.zoomModal = modal;

    this._openZoomFromClick = (event) => {
      if (event.target.closest('button, a, video, iframe, model-viewer, .pdp-media-external-video')) return;
      const slide = event.target.closest('.main-swiper .swiper-slide');
      if (!slide) return;
      const index = this.getMainSwiperSlideIndex(slide);
      this.openProductZoomModal(index);
    };

    this._openZoomFromPinchAttempt = (event) => {
      if (!event.touches || event.touches.length < 2) return;
      if (event.target.closest('video, iframe, model-viewer, .pdp-media-external-video')) return;
      const slide = event.target.closest('.main-swiper .swiper-slide');
      if (!slide) return;
      const index = this.getMainSwiperSlideIndex(slide);
      this.openProductZoomModal(index);
    };

    mainEl.addEventListener('click', this._openZoomFromClick);
    mainEl.addEventListener('touchstart', this._openZoomFromPinchAttempt, { passive: true });
  }

  initProductZoomModalSwiper() {
    const modal = this.zoomModal;

    if (!modal || modal.dataset.zoomInitialized === 'true') return;

    modal.dataset.zoomInitialized = 'true';

    const swiperEl = modal.querySelector('.pdp-zoom-modal__swiper');
    const closeBtn = modal.querySelector('[data-pdp-zoom-close]');
    const paginationEl = modal.querySelector('.pdp-zoom-modal__pagination');
    const prevBtn = modal.querySelector('.pdp-zoom-modal__prev');
    const nextBtn = modal.querySelector('.pdp-zoom-modal__next');

    if (swiperEl && window.Swiper) {
      this.zoomModalSwiper = new Swiper(swiperEl, {
        loop: false,
        slidesPerView: 1,
        spaceBetween: 0,
        touchStartPreventDefault: false,
        passiveListeners: false,
        keyboard: {
          enabled: true,
          onlyInViewport: false,
        },
        zoom: {
          maxRatio: 4,
          minRatio: 1,
          toggle: true,
        },
        pagination: paginationEl
          ? {
              el: paginationEl,
              clickable: true,
            }
          : false,
        navigation: prevBtn && nextBtn
          ? {
              prevEl: prevBtn,
              nextEl: nextBtn,
            }
          : false,
        on: {
          slideChange: () => {
            if (this.zoomModalSwiper?.zoom?.out) this.zoomModalSwiper.zoom.out();
          },
        },
      });

      if (this.zoomModalSwiper?.zoom?.enable) this.zoomModalSwiper.zoom.enable();
    }

    this._closeZoomModal = () => this.closeProductZoomModal();
    this._closeZoomModalOnEsc = (event) => {
      if (event.key === 'Escape' && modal.classList.contains('is-open')) {
        this.closeProductZoomModal();
      }
    };

    closeBtn?.addEventListener('click', this._closeZoomModal);
    document.addEventListener('keydown', this._closeZoomModalOnEsc);

    modal.addEventListener('click', (event) => {
      if (event.target === modal || event.target.classList.contains('pdp-zoom-modal__inner')) {
        this.closeProductZoomModal();
      }
    });
  }

  getMainSwiperSlideIndex(slide) {
    if (!slide) return this.mainSwiper?.activeIndex || 0;

    const mediaId = slide.dataset.media;
    const modalSlides = Array.from(this.zoomModal?.querySelectorAll('.pdp-zoom-modal__swiper .swiper-slide') || []);

    if (mediaId && modalSlides.length) {
      const matchedIndex = modalSlides.findIndex(modalSlide => modalSlide.dataset.media === mediaId);
      if (matchedIndex > -1) return matchedIndex;
    }

    const mainSlides = Array.from(this.querySelectorAll('.main-swiper .swiper-slide'));
    const index = mainSlides.indexOf(slide);
    return index > -1 ? index : (this.mainSwiper?.activeIndex || 0);
  }

  openProductZoomModal(index = 0) {
    if (!this.zoomModal) return;

    this.zoomModal.hidden = false;
    this.zoomModal.setAttribute('aria-hidden', 'false');
    this.zoomModal.classList.add('is-open');
    document.documentElement.classList.add('pdp-zoom-modal-open');
    document.body.classList.add('pdp-zoom-modal-open');

    this.initProductZoomModalSwiper();

    if (this.zoomModalSwiper) {
      this.zoomModalSwiper.slideTo(index, 0);
      this.zoomModalSwiper.update();
      if (this.zoomModalSwiper.zoom?.out) this.zoomModalSwiper.zoom.out();
    }

    window.setTimeout(() => {
      this.zoomModal.querySelector('[data-pdp-zoom-close]')?.focus({ preventScroll: true });
    }, 0);
  }

  closeProductZoomModal() {
    if (!this.zoomModal) return;

    if (this.zoomModalSwiper?.zoom?.out) this.zoomModalSwiper.zoom.out();
    this.zoomModal.classList.remove('is-open');
    this.zoomModal.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('pdp-zoom-modal-open');
    document.body.classList.remove('pdp-zoom-modal-open');

    window.setTimeout(() => {
      if (!this.zoomModal?.classList.contains('is-open')) {
        this.zoomModal.hidden = true;
      }
    }, 180);
  }

  syncThumbSwiperToMain(speed = 200) {
    if (!this.mainSwiper || !this.thumbSwiper) return;

    const activeIndex = typeof this.mainSwiper.realIndex === 'number'
      ? this.mainSwiper.realIndex
      : this.mainSwiper.activeIndex;

    if (!Number.isFinite(activeIndex) || activeIndex < 0) return;

    let visibleCount = 1;
    if (typeof this.thumbSwiper.slidesPerViewDynamic === 'function') {
      visibleCount = this.thumbSwiper.slidesPerViewDynamic() || 1;
    }

    visibleCount = Math.max(1, Math.floor(visibleCount));
    const currentStart = this.thumbSwiper.activeIndex || 0;
    const currentEnd = currentStart + visibleCount - 1;

    let targetStart = currentStart;

    if (activeIndex < currentStart) {
      targetStart = activeIndex;
    } else if (activeIndex > currentEnd) {
      targetStart = activeIndex - visibleCount + 1;
    } else if (activeIndex === currentEnd && !this.thumbSwiper.isEnd) {
      targetStart = currentStart + 1;
    }

    targetStart = Math.max(0, targetStart);

    if (targetStart !== currentStart) {
      this.thumbSwiper.slideTo(targetStart, speed);
    }

    this.updateThumbNavButtons();
  }

  updateMainNavButtons() {
    if (!this.mainSwiper) return;

    this._prevBtn?.classList.toggle(
      'swiper-button-disabled',
      this.mainSwiper.isBeginning
    );

    this._nextBtn?.classList.toggle(
      'swiper-button-disabled',
      this.mainSwiper.isEnd
    );
  }

  updateThumbNavButtons() {
    if (!this.thumbSwiper) return;

    this._thumbPrevBtn?.classList.toggle(
      'swiper-button-disabled',
      this.thumbSwiper.isBeginning
    );

    this._thumbNextBtn?.classList.toggle(
      'swiper-button-disabled',
      this.thumbSwiper.isEnd
    );
  }

  updateMobilePagination() {
    // Swiper handles pagination automatically
  }

  // ─── Selling Plan ─────────────────────────────────────────────────
  initSellingPlan() {
    const widget = this.querySelector('[data-selling-plan-widget]');
    if (!widget) return;

    const hiddenInput = widget.querySelector('#pinfo-selling-plan-input');

    const getVariantId = () => {
      const idInput = this.querySelector('input[name="id"]');
      return idInput ? String(idInput.value) : null;
    };

    const syncDisplay = (spId, priceEl, compareEl, badgeEl, freqSelect) => {
      const variantId   = getVariantId();
      const variantData = variantId ? (this.spAllocations.variants[variantId] || null) : null;
      const alloc       = (variantData && spId) ? (variantData.allocations[String(spId)] || null) : null;

      const price      = alloc ? this.formatMoney(alloc.price) : '';
      const compareRaw = alloc ? alloc.compare_at_price : 0;
      const compare    = (alloc && compareRaw > alloc.price) ? this.formatMoney(compareRaw) : '';

      if (priceEl)   priceEl.textContent = price;
      if (compareEl) compareEl.innerHTML  = compare ? '<s>' + compare + '</s>' : '';

      if (badgeEl && freqSelect) {
        const opt   = freqSelect.options[freqSelect.selectedIndex];
        const badge = opt ? (opt.dataset.badge || '') : '';
        badgeEl.textContent = badge;
        badgeEl.classList.toggle('sp-badge--hidden', !badge);
      }
    };

    widget.querySelectorAll('.sp-widget').forEach(group => {
      const cards         = group.querySelectorAll('.sp-card');
      const subscribeCard = group.querySelector('.sp-card--subscribe');
      const freqSelect    = group.querySelector('[data-frequency-select]');
      const badgeEl       = group.querySelector('[data-badge-display]');
      const priceEl       = group.querySelector('[data-subscribe-price-display]');
      const compareEl     = group.querySelector('[data-subscribe-compare-display]');

      cards.forEach(card => {
        card.addEventListener('click', () => {
          cards.forEach(c => {
            c.classList.remove('active');
            c.querySelector('.sp-hidden-radio').checked = false;
          });
          card.classList.add('active');
          card.querySelector('.sp-hidden-radio').checked = true;

          if (card.dataset.spCard === 'subscribe' && freqSelect) {
            const opt  = freqSelect.options[freqSelect.selectedIndex];
            const spId = opt ? opt.value : '';
            if (hiddenInput) hiddenInput.value = spId;
            syncDisplay(spId, priceEl, compareEl, badgeEl, freqSelect);
          } else {
            if (hiddenInput) hiddenInput.value = '';
          }
        });
      });

      if (freqSelect) {
        freqSelect.addEventListener('change', () => {
          const opt  = freqSelect.options[freqSelect.selectedIndex];
          const spId = opt ? opt.value : '';
          if (subscribeCard?.classList.contains('active') && hiddenInput) {
            hiddenInput.value = spId;
          }
          syncDisplay(spId, priceEl, compareEl, badgeEl, freqSelect);
        });

        freqSelect.addEventListener('click', e => e.stopPropagation());

        const initOpt = freqSelect.options[freqSelect.selectedIndex];
        if (initOpt) syncDisplay(initOpt.value, priceEl, compareEl, badgeEl, freqSelect);
      }
    });

    if (hiddenInput) hiddenInput.value = '';
  }

  // ─── Update selling plan prices when variant changes ──────────────
  updateSellingPlanPrices(variantId) {
    const variantData = this.spAllocations.variants[String(variantId)];

    const oneTimePriceEl = this.querySelector('[data-one-time-price]');
    if (oneTimePriceEl && variantData) {
      oneTimePriceEl.textContent = this.formatMoney(variantData.price);
    }

    const planMap = {};
    (this.productJSON?.selling_plan_groups || []).forEach(g =>
      (g.selling_plans || []).forEach(p => { planMap[p.id] = p; })
    );

    this.querySelectorAll('.sp-widget').forEach(groupEl => {
      const freqSelect = groupEl.querySelector('[data-frequency-select]');
      const badgeEl    = groupEl.querySelector('[data-badge-display]');
      const priceEl    = groupEl.querySelector('[data-subscribe-price-display]');
      const compareEl  = groupEl.querySelector('[data-subscribe-compare-display]');
      if (!freqSelect) return;

      const opt    = freqSelect.options[freqSelect.selectedIndex];
      const spId   = opt ? String(opt.value) : null;
      const alloc  = (variantData && spId) ? (variantData.allocations[spId] || null) : null;

      const price      = alloc ? this.formatMoney(alloc.price) : '';
      const compareRaw = alloc ? alloc.compare_at_price : 0;
      const compare    = (alloc && compareRaw > alloc.price) ? this.formatMoney(compareRaw) : '';

      if (priceEl)   priceEl.textContent = price;
      if (compareEl) compareEl.innerHTML  = compare ? '<s>' + compare + '</s>' : '';

      if (badgeEl && opt && variantData) {
        const plan = planMap[Number(spId)];
        const adj  = plan?.price_adjustments?.[0];
        let badge  = opt.dataset.badge || '';

        if (adj?.value_type === 'price') {
          const saving = variantData.price - adj.value;
          badge = saving > 0 ? 'Save ' + this.formatMoney(saving) : 'Special Price';
          opt.dataset.badge = badge;
        }

        badgeEl.textContent = badge;
        badgeEl.classList.toggle('sp-badge--hidden', !badge);
      }
    });
  }

  // ─── Format money ─────────────────────────────────────────────────
  formatMoney(cents) {
    if (typeof cents !== 'number' || isNaN(cents)) return '';

    if (window.Shopify?.formatMoney) {
      const fmt = window.Shopify.money_format
        || window.Shopify.moneyFormat
        || window.theme?.moneyFormat
        || '${{amount}}';
      const raw = window.Shopify.formatMoney(cents, fmt);
      return raw.replace(/[.,]00(?!\d)/, '');
    }

    const amount   = cents / 100;
    const hasCents = (amount % 1) !== 0;
    const currency = window.Shopify?.currency?.active || 'USD';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: hasCents ? 2 : 0,
    }).format(amount);
  }

  // ─── Accordion ────────────────────────────────────────────────────
initAccordion() {
  const items = this.querySelectorAll('.accordion-item');
  if (!items.length) return;

  const open = (item) => {
    const content = item.querySelector('.accordion-content');
    const trigger = item.querySelector('.accordion-trigger');
    item.classList.add('accordion__active');
    trigger?.setAttribute('aria-expanded', 'true');
    content.style.maxHeight = content.scrollHeight + 'px';
  };

  const close = (item) => {
    const content = item.querySelector('.accordion-content');
    const trigger = item.querySelector('.accordion-trigger');
    item.classList.remove('accordion__active');
    trigger?.setAttribute('aria-expanded', 'false');
    content.style.maxHeight = null;
  };

  items.forEach(item => {
    item.querySelector('.accordion-trigger')?.addEventListener('click', () => {
      const isOpen = item.classList.contains('accordion__active');
      items.forEach(i => close(i));
      if (!isOpen) open(item);
    });
  });
}

  // ─── Form Bindings ────────────────────────────────────────────────
  bindFormSubmit() {
    const form = this.querySelector('form[action$="/cart/add"]');
    if (!form) return;
    form.removeEventListener('submit', this._boundHandleAddToCart);
    this._boundHandleAddToCart = this.handleAddToCart.bind(this);
    form.addEventListener('submit', this._boundHandleAddToCart);
  }

  bindBuyNow() {
    const btn = this.querySelector('.p-info-btn-buy-now');
    if (!btn) return;
    btn.removeEventListener('click', this._boundHandleBuyNow);
    this._boundHandleBuyNow = this.handleBuyNow.bind(this);
    btn.addEventListener('click', this._boundHandleBuyNow);
  }

  handleBuyNow = async (e) => {
    e.preventDefault();

    const btn = e.currentTarget;
    const form = this.getCartAddForm();
    if (!form || !btn || btn.disabled) return;

    this.clearProductError();
    const originalHTML = btn.innerHTML;
    this.setActionButtonLoading(btn, 'Processing...');

    try {
      const data = this.buildCartAddFormData(form, false);
      await this.addItemToCart(data);
      window.location.href = window.routes?.checkout_url || '/checkout';
    } catch (err) {
      console.error('Buy Now failed:', err);
      this.showProductError(this.getProductErrorMessage(err));
      this.restoreActionButton(btn, originalHTML);
    }
  }

  appendSellingPlan(formData) {
    const hiddenInput = this.querySelector('#pinfo-selling-plan-input');
    if (hiddenInput && hiddenInput.value) {
      formData.set('selling_plan', hiddenInput.value);
    } else {
      formData.delete('selling_plan');
    }
  }

  getCartAddForm() {
    return this.querySelector('form[action$="/cart/add"]');
  }

  buildCartAddFormData(form, includeCartSections = true) {
    const data = new FormData(form);
    const variantInput = form.querySelector('[name="id"]') || this.querySelector('[name="id"]');

    if (!variantInput || !variantInput.value) {
      throw new Error('Please select an available option before adding this product.');
    }

    data.set('id', variantInput.value);

    const qty = this.querySelector('.qty-input, .quantity__input, input[name="quantity"]');
    if (qty) {
      const quantity = Math.max(parseInt(qty.value, 10) || 1, 1);
      data.set('quantity', quantity);
      qty.value = quantity;
    }

    this.appendSellingPlan(data);

    if (includeCartSections && this.cart?.getSectionsToRender) {
      data.append('sections', this.cart.getSectionsToRender().map(section => section.id));
      data.append('sections_url', window.location.pathname);
      this.cart.setActiveElement?.(document.activeElement);
    }

    return data;
  }

  async addItemToCart(formData) {
    const response = await fetch((window.Shopify?.routes?.root || '/') + 'cart/add.js', {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: formData
    });

    let parsedState = null;
    try {
      parsedState = await response.json();
    } catch (jsonError) {
      if (!response.ok) {
        throw new Error('We could not add this item to your cart. Please try again.');
      }
    }

    if (!response.ok || parsedState?.status) {
      const message = parsedState?.description || parsedState?.message || parsedState?.errors || 'We could not add this item to your cart. Please try again.';
      throw new Error(Array.isArray(message) ? message.join(' ') : String(message));
    }

    return parsedState;
  }

  setActionButtonLoading(button, loadingText = 'Adding...') {
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.classList.add('loading');
    button.style.opacity = '0.8';

    const textEl = button.querySelector('.atc-text');
    if (textEl) textEl.textContent = loadingText;
    else button.textContent = loadingText;
  }

  restoreActionButton(button, originalHTML) {
    if (!button) return;
    if (originalHTML) button.innerHTML = originalHTML;
    button.disabled = false;
    button.removeAttribute('aria-busy');
    button.classList.remove('loading');
    button.style.opacity = '1';
  }

  getProductErrorMessage(err) {
    const fallback = 'Something went wrong. Please try again or contact us for help.';
    const message = typeof err === 'string' ? err : err?.message;
    if (!message) return fallback;

    if (/sold out|not enough|unavailable|cannot be added|not available/i.test(message)) {
      return 'This item is currently unavailable or there is not enough inventory for the quantity selected.';
    }

    return message;
  }

  getProductErrorWrapper() {
    let wrapper = this.querySelector('.pinfo-form-wrapper .product-form__error-message-wrapper')
      || this.querySelector('.product-form__error-message-wrapper');

    if (!wrapper) {
      const formWrapper = this.querySelector('.pinfo-form-wrapper');
      if (!formWrapper) return null;
      wrapper = document.createElement('div');
      wrapper.className = 'product-form__error-message-wrapper pinfo-product-error';
      wrapper.setAttribute('role', 'alert');
      wrapper.hidden = true;
      wrapper.innerHTML = '<span class="product-form__error-message"></span>';
      formWrapper.prepend(wrapper);
    }

    return wrapper;
  }

  showProductError(message) {
    const wrapper = this.getProductErrorWrapper();
    if (!wrapper) return;

    const errorMessage = wrapper.querySelector('.product-form__error-message') || wrapper;
    errorMessage.textContent = message;
    wrapper.hidden = false;
    wrapper.setAttribute('tabindex', '-1');
    wrapper.focus({ preventScroll: true });
    wrapper.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }

  clearProductError() {
    const wrapper = this.getProductErrorWrapper();
    if (!wrapper) return;

    const errorMessage = wrapper.querySelector('.product-form__error-message');
    if (errorMessage) errorMessage.textContent = '';
    wrapper.hidden = true;
    wrapper.removeAttribute('tabindex');
  }

  handleAddToCart = async (e) => {
    e.preventDefault();

    const btn = this.querySelector('button[type="submit"]');
    const form = this.getCartAddForm();
    if (!form || !btn || btn.disabled) return;

    const originalHTML = btn.innerHTML;
    this.clearProductError();
    this.setActionButtonLoading(btn, 'Adding...');

    this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    let addedSuccessfully = false;

    try {
      const data = this.buildCartAddFormData(form, true);
      const parsedState = await this.addItemToCart(data);
      addedSuccessfully = true;

      if (this.cart?.renderContents) {
        this.cart.renderContents(parsedState);
      } else {
        window.location.href = window.routes?.cart_url || '/cart';
      }

      if (this.cart?.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
      this.closest('.pqv-wrapper')?.querySelector('.pqv-close-btn')?.click();
    } catch (err) {
      console.error('Add to cart failed:', err);
      this.showProductError(this.getProductErrorMessage(err));
    } finally {
      if (!addedSuccessfully || !this.closest('.pqv-wrapper')) {
        this.restoreActionButton(btn, originalHTML);
      }
    }
  }

 handleOptions(url) {
  if (!url) return;

  const scrollY = window.scrollY;

  this.fetchData(url)
    .then(() => {
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: scrollY, left: 0, behavior: 'auto' });
      });
    })
    .catch((error) => {
      console.error('Variant update failed:', error);
      this.showProductError?.('We could not update this option. Please try again.');
      window.requestAnimationFrame(() => {
        window.scrollTo({ top: scrollY, left: 0, behavior: 'auto' });
      });
    });

  this.updateURL(url);
}

async fetchData(url) {
  const separator = url.includes('?') ? '&' : '?';
  const sectionUrl = `${url}${separator}section_id=${this.sectionId}`;

  const resp = await fetch(sectionUrl);
  const txt = await resp.text();
  const html = new DOMParser().parseFromString(txt, 'text/html');

  this.renderEl(html);
}

  renderEl(html) {
    this.querySelectorAll('pinfo-variant-picker').forEach((el, i) => {
      const newEl = html.querySelectorAll('pinfo-variant-picker')[i];
      if (newEl) el.innerHTML = newEl.innerHTML;
    });
    this.querySelectorAll('.pinfo-price-wrapper').forEach((el, i) => {
      const newEl = html.querySelectorAll('.pinfo-price-wrapper')[i];
      if (newEl) el.innerHTML = newEl.innerHTML;
    });
    this.querySelectorAll('.pinfo-form-wrapper').forEach((el, i) => {
      const newEl = html.querySelectorAll('.pinfo-form-wrapper')[i];
      if (newEl) {
        el.innerHTML = newEl.innerHTML;
        this.bindFormSubmit();
        this.bindBuyNow();
      }
    });

    const idInput = this.querySelector('input[name="id"]');
    if (idInput?.value) {
      const vid = Number(idInput.value);
      document.dispatchEvent(new CustomEvent('variant:change', { detail: { variantId: vid } }));
      if (!this.isMobileProductViewport()) this.slideToVariantMedia(vid, 300);
      this.updateSellingPlanPrices(vid);
    }
  }

  updateURL(url) {
    if (typeof url !== 'string' || !url.startsWith('/')) return;
    if (this.dataset?.updateUrl === 'true') {
      window.history.pushState({}, '', url);
    }
  }


}

document.addEventListener('DOMContentLoaded', () => {
  if (!customElements.get('product-information')) {
    customElements.define('product-information', ProductInformation);
  }
});

document.querySelectorAll('.desc-toggle-btn').forEach(function(btn) {
  btn.addEventListener('click', function(e) {
    e.preventDefault();

    var wrapper = btn.closest('.pd-description-wrapper');
    var shortSpan = wrapper.querySelector('.desc-short');
    var fullSpan = wrapper.querySelector('.desc-full');
    var expanded = btn.getAttribute('aria-expanded') === 'true';

    if (expanded) {
      shortSpan.style.display = '';
      fullSpan.classList.add('desc-hidden');
      btn.textContent = 'Read More';
      btn.setAttribute('aria-expanded', 'false');
    } else {
      shortSpan.style.display = 'none';
      fullSpan.classList.remove('desc-hidden');
      btn.textContent = 'Read Less';
      btn.setAttribute('aria-expanded', 'true');
    }
  });
});
